import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Visibility, Edit, Delete, Search } from "@mui/icons-material";
import axios from "axios";
import { ENDPOINT } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const pageSize = 2; // ✅ Django pagination page_size (သင့် backend setting နဲ့ကိုက်အောင်ပြင်ပါ)

  // ---------------- Fetch Users ----------------
  const fetchUsers = async (url = `${ENDPOINT.USER_LIST}?page=1`, searchQuery = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const fullUrl = searchQuery
        ? `${ENDPOINT.USER_LIST}?search=${encodeURIComponent(searchQuery)}`
        : url;

      const response = await axios.get(fullUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;

      const pageMatch = fullUrl.match(/page=(\d+)/);
      let page = pageMatch ? parseInt(pageMatch[1]) : 1;
      if (page > data.total_pages && data.total_pages > 0) {
        return fetchUsers(`${ENDPOINT.USER_LIST}?page=${data.total_pages}`, searchQuery);
      }

      setUsers(data.results || []);
      setNext(data.next);
      setPrevious(data.previous);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        localStorage.clear();
        window.location.href = "/login";
      } else {
        toast.error("Failed to fetch users");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    if (location.state?.toastMessage) {
      toast.success(location.state.toastMessage);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(`${ENDPOINT.USER_LIST}?page=1`, search);
  };

  const handleNext = () => next && fetchUsers(next, search);
  const handlePrevious = () => previous && fetchUsers(previous, search);

  const handleDetail = (id) => navigate(`/users/${id}`);
  const handleEdit = (id) => navigate(`/users/edit/${id}`);

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const closeDeleteDialog = () => {
    setDeleteId(null);
    setOpenDialog(false);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(ENDPOINT.USER_DELETE(deleteId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted successfully!");
      if (users.length === 1 && currentPage > 1) {
        fetchUsers(`${ENDPOINT.USER_LIST}?page=${currentPage - 1}`, search);
      } else {
        fetchUsers(`${ENDPOINT.USER_LIST}?page=${currentPage}`, search);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete user");
    } finally {
      closeDeleteDialog();
    }
  };

  // ---------------- UI ----------------
  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        {/* 🔍 Search Box */}
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            size="small"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
          <Button type="submit" variant="contained" color="primary" sx={{ ml: 1 }} disabled={loading}>
            Search
          </Button>
        </Box>

        {/* ➕ Create User Button */}
        <Button
          variant="contained"
          color="success"
          sx={{ ml: 2, textTransform: "none" }}
          onClick={() => navigate("/users/create")}
        >
          + Create User
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell> {/* ✅ Added */}
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Groups</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {users.length > 0 ? (
                  users.map((user, index) => (
                    <TableRow key={user.id} hover>
                      {/* ✅ Dynamic serial number (auto count per page) */}
                      <TableCell>{(currentPage - 1) * pageSize + index + 1}</TableCell>

                      <TableCell>
                        <Button
                          onClick={() => handleDetail(user.id)}
                          variant="text"
                          sx={{ textTransform: "none" }}
                        >
                          {user.username}
                        </Button>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone || "-"}</TableCell>

                      {/* Groups */}
                      <TableCell>
                        {user.groups?.length > 0 ? (
                          <Tooltip title={user.groups.join(", ")} arrow placement="top">
                            <Chip
                              label={`${user.groups.length} groups`}
                              color="primary"
                              size="small"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No Groups
                          </Typography>
                        )}
                      </TableCell>

                      {/* Permissions */}
                      <TableCell>
                        {user.permissions?.length > 0 ? (
                          <Tooltip title={user.permissions.join(", ")} arrow placement="top">
                            <Chip
                              label={`${user.permissions.length} perms`}
                              color="secondary"
                              size="small"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No Permissions
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Detail">
                            <IconButton color="primary" onClick={() => handleDetail(user.id)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit User">
                            <IconButton color="warning" onClick={() => handleEdit(user.id)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete User">
                            <IconButton color="error" onClick={() => openDeleteDialog(user.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            sx={{ backgroundColor: "#fafafa" }}
          >
            <Typography variant="body2" color="text.secondary">
              Total Users: <strong>{count}</strong> | Total Pages:{" "}
              <strong>{totalPages}</strong>
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" size="small" disabled={!previous} onClick={handlePrevious}>
                ← Prev
              </Button>
              <Typography variant="body2">
                Page {currentPage} / {totalPages}
              </Typography>
              <Button variant="outlined" size="small" disabled={!next} onClick={handleNext}>
                Next →
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserList;
